import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import SocialIcons from "../components/SocialIcons";
import { openInMapsApp, SOCIETY_ADDRESS } from "../lib/maps";

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending" || status === "sent") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/api/chat/leave-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
          source: "contact_form",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus("idle"), 5000);
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        "We couldn't send your message right now. Please try again or email info@welcometosociety.com directly."
      );
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0a0a0a] pt-24 pb-10 lg:pt-28 lg:pb-16">
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 mb-10 lg:mb-20">
        <p className="text-[var(--accent)] text-xs tracking-[0.32em] uppercase mb-5">Let's Talk</p>
        <h1 className="font-display text-white text-[clamp(56px,10vw,170px)] leading-[0.9]">
          Get in
          <br />
          <span className="font-elegant italic font-light">touch.</span>
        </h1>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Info */}
        <div className="lg:col-span-5">
          <p className="text-white/70 leading-relaxed">
            Visit us in Ambridge, or send us a note — we'd love to hear from you. Whether
            you're booking your first ritual or planning a Society takeover, we're here for it.
          </p>

          <ul className="mt-8 lg:mt-12 space-y-5 lg:space-y-7">
            <li className="flex gap-5">
              <span className="w-11 h-11 rounded-full border border-[var(--accent)]/40 text-[var(--accent)] flex items-center justify-center flex-shrink-0">
                <MapPin size={18} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--accent)] mb-1">Location</p>
                <button
                  type="button"
                  onClick={() => openInMapsApp(SOCIETY_ADDRESS)}
                  className="text-left text-white text-lg hover:text-[var(--accent)] transition-colors bg-transparent p-0 border-0 cursor-pointer"
                  data-testid="contact-address-link"
                >
                  551 Merchant Street
                  <span className="block text-white/55 text-sm mt-0.5">Ambridge, PA 15003</span>
                </button>
              </div>
            </li>
            <li className="flex gap-5">
              <span className="w-11 h-11 rounded-full border border-[var(--accent)]/40 text-[var(--accent)] flex items-center justify-center flex-shrink-0">
                <Phone size={18} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--accent)] mb-1">Phone</p>
                <a
                  href="tel:+17243850145"
                  className="text-white text-lg hover:text-[var(--accent)] transition-colors"
                  data-testid="contact-phone-link"
                >
                  724-385-0145
                </a>
              </div>
            </li>
            <li className="flex gap-5">
              <span className="w-11 h-11 rounded-full border border-[var(--accent)]/40 text-[var(--accent)] flex items-center justify-center flex-shrink-0">
                <Mail size={18} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--accent)] mb-1">Email</p>
                <a
                  href="mailto:info@welcometosociety.com"
                  className="text-white text-lg hover:text-[var(--accent)] transition-colors break-all"
                  data-testid="contact-email-link"
                >
                  info@welcometosociety.com
                </a>
              </div>
            </li>
            <li className="flex gap-5">
              <span className="w-11 h-11 rounded-full border border-[var(--accent)]/40 text-[var(--accent)] flex items-center justify-center flex-shrink-0">
                <Clock size={18} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--accent)] mb-1">Hours</p>
                <p className="text-white text-lg">Tue – Sat · 10am – 7pm</p>
                <p className="text-white/55 text-sm mt-0.5">Sun – Mon · Closed</p>
              </div>
            </li>
          </ul>

          <div className="mt-8 lg:mt-12">
            <SocialIcons size="lg" />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            className="bg-[#111113] border border-white/8 rounded-2xl p-6 lg:p-12"
            data-testid="contact-form"
          >
            <h2 className="font-display text-white text-3xl mb-2">Send a message</h2>
            <p className="text-white/55 text-sm mb-7 lg:mb-10">We'll be in touch within 24 hours.</p>

            <div className="space-y-5 lg:space-y-7">
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-white/55 mb-1">Name</label>
                <input
                  className="field"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-white/55 mb-1">Email</label>
                <input
                  type="email"
                  className="field"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-white/55 mb-1">Message</label>
                <textarea
                  className="field resize-none"
                  placeholder="Tell us what you're looking for..."
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  data-testid="contact-input-message"
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn-accent mt-7 lg:mt-10 w-full sm:w-auto"
              data-testid="contact-submit"
              disabled={status === "sending" || status === "sent"}
            >
              {status === "sending"
                ? "Sending…"
                : status === "sent"
                ? "Sent — Thank you!"
                : "Send Message"} <Send size={14} />
            </button>

            {status === "error" && (
              <p
                className="mt-5 text-sm text-red-400"
                data-testid="contact-error"
              >
                {errorMsg}
              </p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
